import { useEffect, useState, useRef, useCallback } from 'react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../store/auth'
import { useToast } from '../ui/Toast'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import type { Attachment } from '@shared/types'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface Props {
  siteId: string
}

export function SiteDocumentsTab({ siteId }: Props) {
  const [documents, setDocuments] = useState<Attachment[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toastError } = useToast()

  const loadDocuments = useCallback(() => {
    api.get<Attachment[]>(`/attachments?entity_type=site_document&entity_id=${encodeURIComponent(siteId)}`)
      .then(setDocuments)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId])

  useEffect(() => { loadDocuments() }, [loadDocuments])

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    const token = useAuthStore.getState().token
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('entity_type', 'site_document')
        formData.append('entity_id', siteId)
        const resp = await fetch('/api/attachments', {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        })
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}))
          throw new Error((err as Record<string, string>).detail || `Upload failed (${resp.status})`)
        }
      }
      loadDocuments()
    } catch (err) { toastError('Failed to upload document', err) }
    finally { setUploading(false) }
  }

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={e => handleUpload(e.target.files)}
      />
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700 }}>Documents</h3>
        <Button loading={uploading} onClick={() => fileInputRef.current?.click()} icon={PlusIcon}>
          Upload Document
        </Button>
      </div>

      {loading ? (
        <div className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-lg)' }} />
      ) : documents.length > 0 ? (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Uploaded</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => (
                <tr key={doc.id} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span style={{ fontSize: 16 }}>📄</span>
                      <span style={{ fontWeight: 500 }}>{doc.file_name}</span>
                    </div>
                  </td>
                  <td>
                    {doc.content_type && (
                      <Badge>{doc.content_type}</Badge>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                    {new Date(doc.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📁</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>No documents</div>
          <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 16 }}>
            Upload warranty cards, config backups, manuals, and contracts.
          </p>
          <Button loading={uploading} onClick={() => fileInputRef.current?.click()} icon={PlusIcon}>
            Upload Document
          </Button>
        </div>
      )}
    </div>
  )
}
